/**
 * Endereço a partir do CEP, pelo ViaCEP.
 *
 * O ViaCEP é público e não pede conta — por isso entra agora, e não fica para
 * "quando houver integração". O que ele pode fazer é falhar: fora do ar, lento,
 * ou "CEP inexistente" com status 200. Cada caso volta com o motivo em
 * português, e quem chama decide o que mostrar; nada aqui apaga o que a pessoa
 * já digitou.
 */

export interface EnderecoDoCep {
    /** Formatado `00000-000`. */
    cep: string;
    rua: string;
    bairro: string;
    cidade: string;
    estado: string;
}

export type ResultadoDoCep =
    | { ok: true; endereco: EnderecoDoCep }
    | { ok: false; motivo: string };

/** Os oito dígitos, ou nulo se não houver oito. */
export function digitosDoCep(valor: string): string | null {
    const digitos = valor.replace(/\D/g, '');
    return digitos.length === 8 ? digitos : null;
}

export async function consultarCep(
    valor: string,
    buscar: typeof fetch = fetch,
    tempoLimiteMs = 6000,
): Promise<ResultadoDoCep> {
    const cep = digitosDoCep(valor);
    if (!cep) return { ok: false, motivo: 'O CEP tem 8 dígitos.' };

    const controle = new AbortController();
    const relogio = setTimeout(() => controle.abort(), tempoLimiteMs);
    try {
        const r = await buscar(`https://viacep.com.br/ws/${cep}/json/`, { signal: controle.signal });
        if (!r.ok) return { ok: false, motivo: 'A consulta de CEP não respondeu agora. Preencha o endereço à mão.' };
        const corpo = await r.json() as {
            erro?: boolean | string; cep?: string; logradouro?: string; bairro?: string; localidade?: string; uf?: string;
        };
        /* O ViaCEP responde 200 com `{ "erro": true }` para CEP que não existe. */
        if (corpo.erro) return { ok: false, motivo: `O CEP ${cep.slice(0, 5)}-${cep.slice(5)} não foi encontrado.` };
        return {
            ok: true,
            endereco: {
                cep: `${cep.slice(0, 5)}-${cep.slice(5)}`,
                rua: corpo.logradouro ?? '',
                bairro: corpo.bairro ?? '',
                cidade: corpo.localidade ?? '',
                estado: corpo.uf ?? '',
            },
        };
    } catch {
        return { ok: false, motivo: 'A consulta de CEP não respondeu agora. Preencha o endereço à mão.' };
    } finally {
        clearTimeout(relogio);
    }
}

/**
 * Junta o que o CEP trouxe ao que já está no formulário: preenche o que está
 * vazio e não sobrescreve o que a pessoa digitou.
 */
export function completarEndereco<T extends { cep: string; rua: string; bairro: string; cidade: string; estado: string }>(
    atual: T,
    doCep: EnderecoDoCep,
): T {
    return {
        ...atual,
        cep: doCep.cep,
        rua: atual.rua.trim() ? atual.rua : doCep.rua,
        bairro: atual.bairro.trim() ? atual.bairro : doCep.bairro,
        cidade: atual.cidade.trim() ? atual.cidade : doCep.cidade,
        estado: atual.estado.trim() ? atual.estado : doCep.estado,
    };
}
